import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import AdminSidebar from '../../components/AdminSidebar';
import AdminTopBar from '../../components/AdminTopBar';
import { formatCurrency } from '../../utils/formatUtils';

export default function AdminDataPool() {
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [stats, setStats] = useState({ totalPriceRecords: 0, totalStores: 0, verifiedBranches: 0 });
    const [trends, setTrends] = useState([]);
    const [shares, setShares] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // NEW: Data Grid State
    const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard' | 'grid'
    const [rawRecords, setRawRecords] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const [filters, setFilters] = useState({});

    // Analysis State
    const [activeTab, setActiveTab] = useState('top-products');
    const [analysisData, setAnalysisData] = useState([]);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    // [Faz 7.3] Sekme gerçek mi demo mu — backend `demo`/`demoReason` döndürürse rozet göster.
    const [analysisMeta, setAnalysisMeta] = useState({ demo: false, demoReason: '' });

    useEffect(() => {
        fetchData();
        fetchAnalysis(activeTab);
    }, []);

    const fetchData = async () => {
        try {
            const params = new URLSearchParams();
            if (filters.branchCity) params.append('city', filters.branchCity);
            if (filters.branchDistrict) params.append('district', filters.branchDistrict);
            if (filters.branchNeighborhood) params.append('neighborhood', filters.branchNeighborhood);

            const [resStats, resTrends, resShares] = await Promise.all([
                fetch(`${API_BASE_URL}/api/admin-data/stats`, { credentials: 'include' }).then(r => r.json()),
                fetch(`${API_BASE_URL}/api/admin-data/price-trends?${params.toString()}`, { credentials: 'include' }).then(r => r.json()),
                fetch(`${API_BASE_URL}/api/admin-data/market-share?${params.toString()}`, { credentials: 'include' }).then(r => r.json())
            ]);

            if (resStats.ok) setStats(resStats.stats);
            if (resTrends.ok) setTrends(resTrends.trends);
            if (resShares.ok) setShares(resShares.shares);
        } catch (err) {
            console.error('Data Pool Fetch Error:', err);
        }
    };

    const fetchRawRecords = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/admin-data/raw-records`, { credentials: 'include' });
            if (res.ok) {
                const j = await res.json();
                setRawRecords(j.data || []);
            }
        } catch (err) {
            console.error('Raw Records Fetch Error:', err);
        }
    };

    // Grid Helpers
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            const newFilters = { ...prev, [key]: value };
            // Cascading Reset: If City changes, clear District and Neighborhood
            if (key === 'branchCity') {
                delete newFilters.branchDistrict;
                delete newFilters.branchNeighborhood;
            }
            // If District changes, clear Neighborhood
            if (key === 'branchDistrict') {
                delete newFilters.branchNeighborhood;
            }
            return newFilters;
        });
    };

    const getProcessedRecords = () => {
        let data = [...rawRecords];

        // Filter
        Object.keys(filters).forEach(key => {
            if (filters[key]) {
                if (key === 'onlyVerified') {
                    data = data.filter(item => item.isVerified === true);
                    return;
                }
                const term = filters[key].toLowerCase();
                const categoricalFields = ['productName', 'category', 'market', 'branchCity', 'branchDistrict', 'branchNeighborhood'];

                if (categoricalFields.includes(key)) {
                    // Exact match for categories/locations to avoid "Konya" matching "Konyaaltı"
                    data = data.filter(item => String(item[key] || '').toLowerCase() === term);
                } else {
                    // Inclusion match for standard fields (if any)
                    data = data.filter(item => String(item[key] || '').toLowerCase().includes(term));
                }
            }
        });

        // Sort
        data.sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
            if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return data;
    };

    const downloadCSV = () => {
        const data = getProcessedRecords();
        const headers = ['Ürün Adı', 'Kategori', 'Fiyat', 'Market', 'İl', 'İlçe', 'Mahalle', 'Tarih'];
        const rows = data.map(r => [
            r.productName, r.category, r.price, r.market, r.branchCity, r.branchDistrict, r.branchNeighborhood, new Date(r.date).toLocaleDateString()
        ]);

        // BOM for Excel (UTF-8) and Semicolon for European/Turkish locale
        const csvContent = "\uFEFF"
            + headers.join(";") + "\n"
            + rows.map(e => e.join(";")).join("\n");

        const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "bbtv_market_data.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Helper for Full Cross-Filtering (Excel Style)
    const getOptionsForColumn = (colId) => {
        // To show only valid options, we filter the data by ALL OTHER active filters
        let sourceData = [...rawRecords];

        Object.keys(filters).forEach(key => {
            // Don't filter by the current column's own active filter
            if (filters[key] && key !== colId) {
                const term = filters[key].toLowerCase();
                const categoricalFields = ['productName', 'category', 'market', 'branchCity', 'branchDistrict', 'branchNeighborhood'];

                if (categoricalFields.includes(key)) {
                    sourceData = sourceData.filter(item => String(item[key] || '').toLowerCase() === term);
                } else {
                    sourceData = sourceData.filter(item => String(item[key] || '').toLowerCase().includes(term));
                }
            }
        });

        return [...new Set(sourceData.map(r => r[colId]))].filter(Boolean).sort();
    };

    const fetchAnalysis = async (tabKey) => {
        setActiveTab(tabKey);
        setAnalysisLoading(true);
        try {
            if (tabKey === 'hal-vs-market') {
                const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
                const query = new URLSearchParams({ whitelistOnly: 'true', take: '200' });
                const res = await fetch(`${API_BASE_URL}/api/admin/pricing/mvp-table?${query.toString()}`, {
                    credentials: 'include',
                    headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {}
                });
                if (res.ok) {
                    const j = await res.json();
                    setAnalysisData(Array.isArray(j.rows) ? j.rows : []);
                    setAnalysisMeta({ demo: false, demoReason: '' });
                } else {
                    setAnalysisData([]);
                    setAnalysisMeta({ demo: false, demoReason: '' });
                }
                return;
            }

            const params = new URLSearchParams();
            if (filters.branchCity) params.append('city', filters.branchCity);
            if (filters.branchDistrict) params.append('district', filters.branchDistrict);
            if (filters.branchNeighborhood) params.append('neighborhood', filters.branchNeighborhood);

            const res = await fetch(`${API_BASE_URL}/api/admin-data/${tabKey}?${params.toString()}`, { credentials: 'include' });
            if (res.ok) {
                const j = await res.json();
                setAnalysisData(j.data || []);
                setAnalysisMeta({ demo: !!j.demo, demoReason: j.demoReason || (j.degraded ? (j.reason || 'veri yok') : '') });
            } else {
                setAnalysisData([]);
                setAnalysisMeta({ demo: false, demoReason: '' });
            }
        } catch (err) {
            console.error('Analysis Error:', err);
        } finally {
            setAnalysisLoading(false);
        }
    };

    // [Öneri 1] Admin manuel outlier durum değişimi (accepted | ignored).
    const updateReportStatus = async (id, status) => {
        try {
            await fetch(`${API_BASE_URL}/api/admin-data/regional-report/${id}/status`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status })
            });
            fetchAnalysis('regional-outliers');
        } catch (err) {
            console.error('Report status update error:', err);
        }
    };

    const renderAnalysisContent = () => {
        if (analysisLoading) return <div className="p-12 flex justify-center"><span className="material-symbols-outlined animate-spin text-slate-400 text-3xl">sync</span></div>;
        if (!analysisData.length) return <div className="p-8 text-center text-slate-400 text-sm italic">Bu kategori için yeterli veri bulunamadı.</div>;

        if (activeTab === 'regional-prices') {
            const tierBadge = (t) => {
                const map = {
                    'ilçe-eşik': 'bg-emerald-100 text-emerald-700',
                    'il-eşik': 'bg-blue-100 text-blue-700',
                    'ulusal-eşik': 'bg-violet-100 text-violet-700',
                    'yetersiz': 'bg-slate-200 text-slate-500'
                };
                return map[t] || 'bg-slate-200 text-slate-500';
            };
            const tierCounts = analysisData.reduce((acc, r) => { acc[r.tier] = (acc[r.tier] || 0) + 1; return acc; }, {});
            return (
                <div className="overflow-x-auto">
                    {/* [Öneri 2] Görünürlük: pipeline kaç kademede aktif */}
                    <div className="mb-4 flex flex-wrap gap-2 text-[11px] font-bold">
                        <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">İlçe-eşik: {tierCounts['ilçe-eşik'] || 0}</span>
                        <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700">İl-eşik: {tierCounts['il-eşik'] || 0}</span>
                        <span className="px-3 py-1 rounded-full bg-violet-100 text-violet-700">Ulusal-eşik: {tierCounts['ulusal-eşik'] || 0}</span>
                        <span className="px-3 py-1 rounded-full bg-slate-200 text-slate-500">Yetersiz: {tierCounts['yetersiz'] || 0}</span>
                        <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-400">Toplam grup: {analysisData.length}</span>
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-200 dark:border-slate-700">
                                <th className="py-2 pr-3">Ürün</th>
                                <th className="py-2 pr-3">İl</th>
                                <th className="py-2 pr-3">İlçe</th>
                                <th className="py-2 pr-3 text-right">Median</th>
                                <th className="py-2 pr-3 text-right">Kullanıcı</th>
                                <th className="py-2 pr-3 text-right">Örnek</th>
                                <th className="py-2 pr-3">Kademe</th>
                            </tr>
                        </thead>
                        <tbody>
                            {analysisData.map((item, i) => (
                                <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                                    <td className="py-2 pr-3 font-semibold text-slate-800 dark:text-white capitalize">{item.name}</td>
                                    <td className="py-2 pr-3 text-slate-600 dark:text-slate-300">{item.city}</td>
                                    <td className="py-2 pr-3 text-slate-600 dark:text-slate-300">{item.district}</td>
                                    <td className="py-2 pr-3 text-right font-bold text-primary">{formatCurrency(item.median)}<span className="text-[10px] text-slate-400">/{item.unit}</span></td>
                                    <td className="py-2 pr-3 text-right font-semibold">{item.userCount}</td>
                                    <td className="py-2 pr-3 text-right text-slate-500">{item.sampleSize}</td>
                                    <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${tierBadge(item.tier)}`}>{item.tier}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p className="mt-3 text-[11px] text-slate-400 italic">Kaynak: kullanıcı fiyat raporları (PriceReport). Kullanıcı = farklı kişi sayısı (kullanıcı+gün dedupe). İlçe-eşik ≥3, il ≥5, ulusal ≥10.</p>
                </div>
            );
        }

        if (activeTab === 'regional-outliers') {
            return (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-200 dark:border-slate-700">
                                <th className="py-2 pr-3">Ürün</th>
                                <th className="py-2 pr-3">İl / İlçe</th>
                                <th className="py-2 pr-3 text-right">Fiyat</th>
                                <th className="py-2 pr-3">Durum</th>
                                <th className="py-2 pr-3 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {analysisData.map((item) => (
                                <tr key={item.id} className="border-b border-slate-100 dark:border-slate-800">
                                    <td className="py-2 pr-3 font-semibold text-slate-800 dark:text-white capitalize">{item.name}</td>
                                    <td className="py-2 pr-3 text-slate-600 dark:text-slate-300">{item.city} / {item.district}</td>
                                    <td className="py-2 pr-3 text-right font-bold text-slate-700 dark:text-slate-200">{formatCurrency(item.normalizedPrice)}<span className="text-[10px] text-slate-400">/{item.unit}</span></td>
                                    <td className="py-2 pr-3">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${item.status === 'outlier' ? 'bg-rose-100 text-rose-700' : 'bg-slate-300 text-slate-700'}`}>
                                            {item.status === 'outlier' ? 'aykırı' : 'gizli'}
                                        </span>
                                    </td>
                                    <td className="py-2 pr-3 text-right">
                                        <div className="inline-flex gap-1">
                                            <button onClick={() => updateReportStatus(item.id, 'accepted')} className="px-2 py-1 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-bold hover:bg-emerald-200">Kabul Et</button>
                                            {item.status !== 'ignored' && (
                                                <button onClick={() => updateReportStatus(item.id, 'ignored')} className="px-2 py-1 rounded-md bg-slate-200 text-slate-600 text-[10px] font-bold hover:bg-slate-300">Gizle</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p className="mt-3 text-[11px] text-slate-400 italic">Aykırı (otomatik, sapma &gt;%60) veya gizli (manuel) raporlar. "Kabul Et" median'a geri katar; "Gizle" tamamen çıkarır. Kayıt SİLİNMEZ.</p>
                </div>
            );
        }

        if (activeTab === 'top-products') {
            return (
                <div className="space-y-3">
                    {analysisData.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                            <div className="flex items-center gap-3">
                                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700 text-xs font-bold text-slate-600 dark:text-slate-300">#{i + 1}</span>
                                <span className="font-bold text-slate-800 dark:text-white capitalize">{item.name}</span>
                            </div>
                            <span className="text-sm font-semibold text-primary">{item.count} Kez Tarandı</span>
                        </div>
                    ))}
                </div>
            );
        }

        if (activeTab === 'cheapest-market') {
            return (
                <div className="space-y-3">
                    {analysisData.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                            <div className="flex items-center gap-3">
                                <span className={`w-8 h-8 flex items-center justify-center rounded-lg text-lg ${i === 0 ? 'bg-amber-100 text-amber-600' : 'bg-slate-200 text-slate-500'}`}>
                                    {i === 0 ? '👑' : i + 1}
                                </span>
                                <div>
                                    <p className="font-bold text-slate-800 dark:text-white">{item.name}</p>
                                    <p className="text-xs text-slate-500">Ortalama Sepet Tutarı</p>
                                </div>
                            </div>
                            <span className="text-lg font-black text-emerald-600">{formatCurrency(item.avg)}</span>
                        </div>
                    ))}
                </div>
            );
        }

        if (activeTab === 'unit-price-champions') {
            return (
                <div className="space-y-3">
                    {analysisData.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border-l-4 border-emerald-500">
                            <div>
                                <p className="text-xs font-bold text-slate-400 uppercase mb-1">{item.category}</p>
                                <p className="font-bold text-slate-800 dark:text-white">{item.product}</p>
                                <p className="text-xs text-slate-500 mt-1">{item.market}</p>
                            </div>
                            <div className="text-right">
                                <span className="block text-lg font-black text-emerald-600">{formatCurrency(item.unitPrice)}</span>
                                <span className="text-xs text-slate-400 line-through">{formatCurrency(item.price)} (Paket)</span>
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        if (activeTab === 'seasonal-deviation') {
            return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {analysisData.map((item, i) => {
                        const isUp = item.change.includes('+');
                        return (
                            <div key={i} className={`p-4 rounded-xl border ${isUp ? 'bg-red-50 border-red-100 dark:bg-red-900/10 dark:border-red-900/30' : 'bg-green-50 border-green-100 dark:bg-green-900/10 dark:border-green-900/30'} flex flex-col justify-between`}>
                                <div>
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-bold text-slate-900 dark:text-white">{item.name}</h3>
                                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${isUp ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'}`}>{item.change}</span>
                                    </div>
                                    <p className="text-xs text-slate-500 mb-4">{item.season} Dönemi</p>
                                </div>
                                <div>
                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="text-slate-500">Geçen Yıl:</span>
                                        <span className="font-medium line-through decoration-slate-400">{formatCurrency(item.historicPrice)}</span>
                                    </div>
                                    <div className="flex justify-between text-base font-bold">
                                        <span className="text-slate-700 dark:text-slate-300">Şimdi:</span>
                                        <span className={isUp ? 'text-red-600' : 'text-green-600'}>{formatCurrency(item.currentPrice)}</span>
                                    </div>
                                    <p className="mt-3 text-xs italic opacity-75 border-t pt-2 border-black/5 dark:border-white/5">{item.advice}</p>
                                </div>
                            </div>
                        )
                    })}
                </div>
            );
        }

        if (activeTab === 'allergen-score') {
            return (
                <div className="space-y-4">
                    {analysisData.map((item, i) => (
                        <div key={i} className="flex items-center gap-4 bg-white dark:bg-[#101922] p-4 rounded-xl border border-slate-100 dark:border-slate-800">
                            <div className={`w-12 h-12 flex items-center justify-center rounded-full font-black text-white text-lg shadow-lg ${item.score > 90 ? 'bg-emerald-500' : item.score > 70 ? 'bg-blue-500' : 'bg-amber-500'}`}>
                                {item.score}
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                    <h4 className="font-bold text-slate-900 dark:text-white">{item.market}</h4>
                                    <span className="text-xs font-bold bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full text-slate-600 dark:text-slate-400">{item.label}</span>
                                </div>
                                <p className="text-sm text-slate-500">{item.desc}</p>
                                <div className="mt-2 h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${item.score > 90 ? 'bg-emerald-500' : item.score > 70 ? 'bg-blue-500' : 'bg-amber-500'}`} style={{ width: `${item.score}%` }}></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        if (activeTab === 'smart-substitution') {
            return (
                <div className="space-y-4">
                    {analysisData.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-4 bg-indigo-50 dark:bg-indigo-900/10 rounded-xl border border-indigo-100 dark:border-indigo-900/30">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="material-symbols-outlined text-red-400 text-lg">close</span>
                                    <span className="text-sm text-slate-500 line-through">{item.target}</span>
                                    <span className="text-xs font-bold text-slate-400 bg-white dark:bg-slate-800 px-1.5 rounded">{formatCurrency(item.targetPrice)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-green-500 text-lg">check</span>
                                    <span className="text-base font-bold text-slate-900 dark:text-white">{item.sub}</span>
                                    <span className="text-sm font-bold text-green-600 bg-white dark:bg-slate-800 px-1.5 rounded">{formatCurrency(item.subPrice)}</span>
                                </div>
                            </div>
                            <div className="text-right pl-4 border-l border-indigo-200 dark:border-indigo-800">
                                <span className="block text-2xl font-black text-indigo-600 dark:text-indigo-400">{item.save}</span>
                                <span className="text-xs font-bold text-indigo-400 dark:text-indigo-300">TASARRUF</span>
                                <p className="text-[10px] text-slate-400 mt-1">{item.market}</p>
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        if (activeTab === 'hidden-inflation' || activeTab === 'price-alerts') { // Reuse similar layout
            return (
                <div className="space-y-3">
                    {analysisData.map((item, i) => (
                        <div key={i} className="p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-lg">
                            <div className="flex justify-between mb-2">
                                <span className="font-bold text-red-700 dark:text-red-400 capitalize">{item.name}</span>
                                <span className="bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200 text-[10px] font-bold px-2 py-0.5 rounded-full">{item.alert || `${item.diff}% FARK`}</span>
                            </div>
                            {item.method ? (
                                // Hidden Inflation specific
                                <div className="flex justify-between text-xs items-center">
                                    <div className="flex items-center gap-2">
                                        <span className="line-through text-slate-400">{item.old}</span>
                                        <span className="material-symbols-outlined text-[10px] text-slate-400">arrow_forward</span>
                                        <span className="font-bold text-red-600">{item.new}</span>
                                    </div>
                                    <span className="text-slate-600 font-medium">{item.price}</span>
                                </div>
                            ) : (
                                // Price Alert specific
                                <div className="flex justify-between text-xs">
                                    <div>
                                        <span className="block text-slate-500">En Düşük</span>
                                        <span className="font-bold text-slate-800 dark:text-white">{formatCurrency(item.min)} ({item.minMarket})</span>
                                    </div>
                                    <div className="text-right">
                                        <span className="block text-slate-500">En Yüksek</span>
                                        <span className="font-bold text-slate-800 dark:text-white">{formatCurrency(item.max)} ({item.maxMarket})</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            );
        }

        if (activeTab === 'hal-vs-market') {
            return (
                <div className="space-y-3">
                    {analysisData.map((item, i) => {
                        const spread = Number(item.spreadRatio || 0);
                        const spreadPct = Number(item.spreadPct || 0);
                        const badgeClass =
                            item.flag === 'spread_extreme'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                : item.flag === 'spread_high'
                                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
                        return (
                            <div key={item.id || i} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-700/50">
                                <div className="flex items-center justify-between mb-2">
                                    <div>
                                        <p className="font-bold text-slate-900 dark:text-white capitalize">{item.name}</p>
                                        <p className="text-[10px] uppercase font-black text-slate-400 tracking-widest">{item.canonicalKey || '-'}</p>
                                    </div>
                                    <span className={`text-[10px] font-black px-2 py-1 rounded-full ${badgeClass}`}>
                                        {item.flag || 'spread_unknown'}
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                    <div className="bg-white dark:bg-slate-900 rounded-md p-2">
                                        <p className="text-slate-400">Market</p>
                                        <p className="font-bold text-slate-800 dark:text-slate-200">{formatCurrency(item.marketMedian)}</p>
                                    </div>
                                    <div className="bg-white dark:bg-slate-900 rounded-md p-2">
                                        <p className="text-slate-400">Hal Ref</p>
                                        <p className="font-bold text-slate-800 dark:text-slate-200">{formatCurrency(item.halRef)}</p>
                                    </div>
                                    <div className="bg-white dark:bg-slate-900 rounded-md p-2">
                                        <p className="text-slate-400">Spread</p>
                                        <p className="font-bold text-slate-800 dark:text-slate-200">{Number.isFinite(spread) ? spread.toFixed(2) : '-'}</p>
                                    </div>
                                    <div className="bg-white dark:bg-slate-900 rounded-md p-2">
                                        <p className="text-slate-400">Spread %</p>
                                        <p className="font-bold text-slate-800 dark:text-slate-200">{Number.isFinite(spreadPct) ? `%${spreadPct.toFixed(1)}` : '-'}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }
    };

    return (
        <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
            <AdminSidebar mobileOpen={mobileSidebarOpen} onCloseMobile={() => setMobileSidebarOpen(false)} />

            <main className="flex-1 flex flex-col min-w-0 overflow-hidden md:ml-[var(--admin-sidebar-width,18rem)] relative transition-[margin] duration-300">
                <AdminTopBar title="Merkezi Veri Havuzu" onToggleMobile={() => setMobileSidebarOpen(open => !open)} />

                <div className="flex-1 overflow-y-auto p-4 sm:p-8">
                    <div className="max-w-[1200px] mx-auto space-y-8 pb-10">
                        {/* Header Section Filter Drill-Down */}
                        <div className="flex flex-col gap-6">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div>
                                    <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Merkezi Veri Havuzu</h1>
                                    <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">Hiper-lokal market fiyatları ve akıllı ekonomi analizi.</p>
                                </div>
                                <div className="flex flex-wrap items-center gap-3">
                                    {/* View Toggle */}
                                    <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
                                        <button
                                            onClick={() => setViewMode('dashboard')}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${viewMode === 'dashboard' ? 'bg-white dark:bg-[#101922] shadow-sm text-slate-800 dark:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            <span className="material-symbols-outlined text-sm">dashboard</span>
                                            <span className="hidden sm:inline">Paneller</span>
                                        </button>
                                        <button
                                            onClick={() => { setViewMode('grid'); if (!rawRecords.length) fetchRawRecords(); }}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${viewMode === 'grid' ? 'bg-white dark:bg-[#101922] shadow-sm text-slate-800 dark:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            <span className="material-symbols-outlined text-sm">table_view</span>
                                            <span className="hidden sm:inline">Veri Listesi</span>
                                        </button>
                                    </div>

                                    {viewMode === 'dashboard' && (
                                        <>
                                            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-1 hidden xl:block"></div>

                                            {/* City Selector */}
                                            <select
                                                value={filters.branchCity || ''}
                                                onChange={(e) => handleFilterChange('branchCity', e.target.value)}
                                                className="px-3 py-2 bg-white dark:bg-[#101922] border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-bold text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                            >
                                                <option value="">Tüm Şehirler</option>
                                                <option value="Antalya">Antalya</option>
                                                <option value="İstanbul">İstanbul</option>
                                                <option value="İzmir">İzmir</option>
                                            </select>
                                            {/* District Selector (Cascading) */}
                                            <select
                                                value={filters.branchDistrict || ''}
                                                onChange={(e) => handleFilterChange('branchDistrict', e.target.value)}
                                                className="px-3 py-2 bg-white dark:bg-[#101922] border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-bold text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                            >
                                                <option value="">Tüm İlçeler</option>
                                                {filters.branchCity === 'Antalya' && (
                                                    <>
                                                        <option value="Konyaaltı">Konyaaltı</option>
                                                        <option value="Muratpaşa">Muratpaşa</option>
                                                        <option value="Kepez">Kepez</option>
                                                    </>
                                                )}
                                                {filters.branchCity === 'İstanbul' && (
                                                    <>
                                                        <option value="Kadıköy">Kadıköy</option>
                                                        <option value="Beşiktaş">Beşiktaş</option>
                                                        <option value="Şişli">Şişli</option>
                                                    </>
                                                )}
                                                {filters.branchCity === 'İzmir' && (
                                                    <>
                                                        <option value="Karşıyaka">Karşıyaka</option>
                                                        <option value="Bornova">Bornova</option>
                                                        <option value="Konak">Konak</option>
                                                    </>
                                                )}
                                            </select>

                                            {/* Neighborhood Selector (Cascading) */}
                                            <select
                                                value={filters.branchNeighborhood || ''}
                                                onChange={(e) => handleFilterChange('branchNeighborhood', e.target.value)}
                                                className="px-3 py-2 bg-white dark:bg-[#101922] border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-bold text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                            >
                                                <option value="">Tüm Mahalleler</option>
                                                {/* Antalya Neighborhoods */}
                                                {filters.branchDistrict === 'Konyaaltı' && (
                                                    <>
                                                        <option value="Gürsu">Gürsu</option>
                                                        <option value="Liman">Liman</option>
                                                        <option value="Altınkum">Altınkum</option>
                                                    </>
                                                )}
                                                {filters.branchDistrict === 'Muratpaşa' && (
                                                    <>
                                                        <option value="Lara">Lara</option>
                                                        <option value="Kızıltoprak">Kızıltoprak</option>
                                                        <option value="Meltem">Meltem</option>
                                                    </>
                                                )}
                                                {filters.branchDistrict === 'Kepez' && (
                                                    <>
                                                        <option value="Dokuma">Dokuma</option>
                                                        <option value="Varsak">Varsak</option>
                                                    </>
                                                )}
                                                {/* İstanbul Neighborhoods */}
                                                {filters.branchDistrict === 'Kadıköy' && (
                                                    <>
                                                        <option value="Moda">Moda</option>
                                                        <option value="Caddebostan">Caddebostan</option>
                                                        <option value="Fikirtepe">Fikirtepe</option>
                                                    </>
                                                )}
                                                {filters.branchDistrict === 'Beşiktaş' && (
                                                    <>
                                                        <option value="Bebek">Bebek</option>
                                                        <option value="Etiler">Etiler</option>
                                                        <option value="Çarşı">Çarşı</option>
                                                    </>
                                                )}
                                                {filters.branchDistrict === 'Şişli' && (
                                                    <>
                                                        <option value="Nişantaşı">Nişantaşı</option>
                                                        <option value="Mecidiyeköy">Mecidiyeköy</option>
                                                    </>
                                                )}
                                                {/* İzmir Neighborhoods */}
                                                {filters.branchDistrict === 'Karşıyaka' && (
                                                    <>
                                                        <option value="Bostanlı">Bostanlı</option>
                                                        <option value="Mavişehir">Mavişehir</option>
                                                        <option value="Aksoy">Aksoy</option>
                                                    </>
                                                )}
                                                {filters.branchDistrict === 'Bornova' && (
                                                    <>
                                                        <option value="Özkanlar">Özkanlar</option>
                                                        <option value="Küçükpark">Küçükpark</option>
                                                        <option value="Evka-3">Evka-3</option>
                                                    </>
                                                )}
                                                {filters.branchDistrict === 'Konak' && (
                                                    <>
                                                        <option value="Alsancak">Alsancak</option>
                                                        <option value="Göztepe">Göztepe</option>
                                                    </>
                                                )}
                                            </select>
                                        </>
                                    )}

                                    <button
                                        onClick={() => { fetchData(); fetchAnalysis(activeTab); if (viewMode === 'grid') fetchRawRecords(); }}
                                        className="px-4 py-2 bg-primary text-white rounded-xl shadow-lg shadow-primary/30 text-sm font-bold hover:bg-primary-dark transition-colors flex items-center gap-2"
                                    >
                                        <span className="material-symbols-outlined text-sm">refresh</span>
                                        Analiz Et
                                    </button>
                                </div>
                            </div>
                        </div>

                        {viewMode === 'dashboard' ? (
                            <>
                                {/* Smart Analysis Panel (Advanced) */}
                                <div className="bg-white dark:bg-[#101922] rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                                    {/* ... Existing Dashboard Content ... */}
                                    <div className="p-6 border-b border-slate-100 dark:border-slate-800/50 flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                                        <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                            <span className="material-symbols-outlined text-amber-500">lightbulb</span>
                                            Akıllı Piyasa Analizleri
                                        </h2>
                                        <div className="flex flex-wrap gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
                                            {[
                                                { id: 'regional-prices', label: '📍 İl/İlçe Fiyat' },
                                                { id: 'regional-outliers', label: '🚩 İşaretlenenler' },
                                                { id: 'top-products', label: '🏆 En Çok Taranan' },
                                                { id: 'cheapest-market', label: '📉 En Uygun Market' },
                                                { id: 'hal-vs-market', label: 'Hal vs Market' },
                                                { id: 'unit-price-champions', label: '⚖️ Birim Fiyat (L/kg)' },
                                                { id: 'seasonal-deviation', label: '🍎 Mevsimsel Sapma' },
                                                { id: 'allergen-score', label: '👶 Alerjen Puanı' },
                                                { id: 'smart-substitution', label: '🛒 Akıllı İkame' },
                                                { id: 'hidden-inflation', label: '📊 Gizli Zam' },
                                            ].map(tab => (
                                                <button
                                                    key={tab.id}
                                                    onClick={() => fetchAnalysis(tab.id)}
                                                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-white dark:bg-[#101922] text-slate-900 dark:text-white shadow-sm ring-1 ring-slate-200 dark:ring-slate-700' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'}`}
                                                >
                                                    {tab.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="p-6 bg-slate-50/50 dark:bg-slate-900/50 min-h-[200px]">
                                        {/* [Faz 7.3] Gerçek olmayan sekmeyi dürüstçe işaretle */}
                                        {analysisMeta.demo && (
                                            <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-300/50 bg-amber-50 dark:border-amber-700/40 dark:bg-amber-900/20 px-3 py-2">
                                                <span className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-base">science</span>
                                                <span className="text-xs font-bold text-amber-700 dark:text-amber-300">
                                                    ÖRNEK / DEMO VERİ — gerçek değil{analysisMeta.demoReason ? ` (${analysisMeta.demoReason})` : ''}
                                                </span>
                                            </div>
                                        )}
                                        {renderAnalysisContent()}
                                    </div>
                                </div>

                                {/* Stats Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                    {[
                                        { label: 'Toplam Fiyat Verisi', value: stats.totalPriceRecords, icon: 'bar_chart', color: 'blue' },
                                        { label: 'Kayıtlı Market', value: stats.totalStores, icon: 'storefront', color: 'purple' },
                                        { label: 'Onaylı Şube (3+ Fiş)', value: stats.verifiedBranches, icon: 'verified', color: 'emerald' },
                                    ].map((s, idx) => (
                                        <div key={idx} className="bg-white dark:bg-[#101922] p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
                                            <div className={`w-12 h-12 rounded-xl bg-${s.color}-50 dark:bg-${s.color}-900/20 flex items-center justify-center text-${s.color}-600`}>
                                                <span className="material-symbols-outlined">{s.icon}</span>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">{s.label}</p>
                                                <p className="text-2xl font-black text-slate-900 dark:text-white">{s.value}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                                    {/* Price Trends */}
                                    <div className="bg-white dark:bg-[#101922] rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col h-[500px]">
                                        <div className="p-6 border-b border-slate-100 dark:border-slate-800/50 flex flex-col sm:flex-row gap-4 justify-between items-center bg-white dark:bg-[#101922] z-10">
                                            <h2 className="text-lg font-bold text-slate-900 dark:text-white whitespace-nowrap">Son Fiyat Hareketleri</h2>
                                            <div className="relative w-full sm:w-64">
                                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">search</span>
                                                <input
                                                    type="text"
                                                    placeholder="Ürün ara... (örn: Domates)"
                                                    className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-slate-700 dark:text-slate-200"
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex-1 overflow-y-auto custom-scrollbar">
                                            <table className="w-full text-left border-collapse">
                                                <thead className="sticky top-0 bg-slate-50/95 dark:bg-[#0d141c]/95 backdrop-blur-sm z-10 shadow-sm">
                                                    <tr>
                                                        <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Ürün</th>
                                                        <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Market / Bölge</th>
                                                        <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Fiyat</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                                                    {trends.filter(t => !searchTerm || t.ingredient.name.toLowerCase().includes(searchTerm.toLowerCase())).map((t, idx) => (
                                                        <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/10 transition-colors">
                                                            <td className="px-6 py-4">
                                                                <span className="font-bold text-slate-900 dark:text-white">{t.ingredient.name}</span>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div className="flex flex-col">
                                                                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{t.branch.market.name}</span>
                                                                    <span className="text-xs text-slate-400">{t.branch.city}, {t.branch.district} ({t.branch.neighborhood})</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-sm font-bold">
                                                                    {formatCurrency(t.price)}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {!trends.length && (
                                                        <tr>
                                                            <td colSpan="3" className="px-6 py-12 text-center text-slate-400 dark:text-slate-500 italic">Henüz veri toplanmadı.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Market Share Analysis */}
                                    <div className="bg-white dark:bg-[#101922] rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 space-y-6 h-[500px] overflow-y-auto custom-scrollbar">
                                        <h2 className="text-lg font-bold text-slate-900 dark:text-white sticky top-0 bg-white dark:bg-[#101922] pb-4 z-10">Pazar Payı (Veri Yoğunluğu)</h2>
                                        <div className="space-y-6">
                                            {shares.map((m, idx) => {
                                                const maxRecords = Math.max(...shares.map(s => s.records), 1);
                                                const percent = (m.records / maxRecords) * 100;
                                                return (
                                                    <div key={idx} className="space-y-2">
                                                        <div className="flex justify-between items-end">
                                                            <div>
                                                                <span className="font-bold text-slate-900 dark:text-white">{m.name}</span>
                                                                <span className="ml-2 text-xs font-bold text-slate-400 dark:text-slate-500">{m.branches} Şube</span>
                                                            </div>
                                                            <span className="text-sm font-black text-primary">{m.records} Kayıt</span>
                                                        </div>
                                                        <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full bg-primary rounded-full transition-all duration-1000"
                                                                style={{ width: `${percent}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {!shares.length && (
                                                <div className="py-12 text-center text-slate-400 dark:text-slate-500 italic">Analiz için yeterli veri yok.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            // GRID VIEW (EXCEL MODE)
                            <div className="bg-white dark:bg-[#101922] rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col h-[700px]">
                                <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/50">
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-2">
                                            <span className="material-symbols-outlined text-green-600">table_view</span>
                                            <span className="font-bold text-slate-700 dark:text-slate-200 text-sm">Gelişmiş Veri Listesi ({rawRecords.length} Satır)</span>
                                        </div>
                                        <div className="flex items-center gap-2 bg-white/50 dark:bg-slate-800/50 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700">
                                            <input
                                                type="checkbox"
                                                id="verifiedOnly"
                                                className="w-4 h-4 accent-green-600 rounded cursor-pointer"
                                                checked={!!filters.onlyVerified}
                                                onChange={(e) => handleFilterChange('onlyVerified', e.target.checked)}
                                            />
                                            <label htmlFor="verifiedOnly" className="text-xs font-bold text-slate-600 dark:text-slate-400 cursor-pointer select-none">Sadece Doğrulanmış</label>
                                        </div>
                                    </div>
                                    <button
                                        onClick={downloadCSV}
                                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-bold flex items-center gap-2 hover:bg-green-700 transition"
                                    >
                                        <span className="material-symbols-outlined text-base">download</span>
                                        Excel İndir
                                    </button>
                                </div>
                                <div className="flex-1 overflow-auto custom-scrollbar relative">
                                    <table className="w-full text-left border-collapse min-w-[1000px]">
                                        <thead className="sticky top-0 bg-slate-100 dark:bg-slate-800 z-20 shadow-sm text-xs font-bold text-slate-500 uppercase">
                                            <tr>
                                                {[
                                                    { id: 'productName', label: 'Ürün Adı' },
                                                    { id: 'category', label: 'Kategori' },
                                                    { id: 'market', label: 'Market' },
                                                    { id: 'branchCity', label: 'İl' },
                                                    { id: 'branchDistrict', label: 'İlçe' },
                                                    { id: 'branchNeighborhood', label: 'Mahalle' },
                                                    { id: 'price', label: 'Fiyat', noFilter: true },
                                                    { id: 'date', label: 'Tarih', noFilter: true }
                                                ].map(col => (
                                                    <th key={col.id} className="p-2 border-b border-r border-slate-200 dark:border-slate-700 last:border-r-0 min-w-[150px]">
                                                        <div
                                                            className="flex items-center gap-1 cursor-pointer hover:text-slate-800 dark:hover:text-white"
                                                            onClick={() => handleSort(col.id)}
                                                        >
                                                            {col.label}
                                                            {sortConfig.key === col.id && (
                                                                <span className="material-symbols-outlined text-xs">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>
                                                            )}
                                                        </div>
                                                        {/* Dynamic Dropdown Filter */}
                                                        {!col.noFilter ? (
                                                            <select
                                                                className="mt-1 w-full px-2 py-1 text-[10px] font-normal border rounded bg-white dark:bg-slate-900 dark:border-slate-700 focus:outline-none focus:border-primary"
                                                                value={filters[col.id] || ''}
                                                                onChange={(e) => handleFilterChange(col.id, e.target.value)}
                                                            >
                                                                <option value="">Tümü</option>
                                                                {getOptionsForColumn(col.id).map((val, idx) => (
                                                                    <option key={idx} value={val}>{val}</option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <div className="h-6"></div> // Spacer
                                                        )}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                                            {getProcessedRecords().map((row) => (
                                                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/20">
                                                    <td className="p-3 border-r border-slate-100 dark:border-slate-800 font-medium text-slate-900 dark:text-white">{row.productName}</td>
                                                    <td className="p-3 border-r border-slate-100 dark:border-slate-800 text-slate-500">{row.category}</td>
                                                    <td className="p-3 border-r border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-300">{row.market}</td>
                                                    <td className="p-3 border-r border-slate-100 dark:border-slate-800 text-slate-500">{row.branchCity}</td>
                                                    <td className="p-3 border-r border-slate-100 dark:border-slate-800 text-slate-500">{row.branchDistrict}</td>
                                                    <td className="p-3 border-r border-slate-100 dark:border-slate-800 text-slate-500">{row.branchNeighborhood}</td>
                                                    <td className="p-3 border-r border-slate-100 dark:border-slate-800 font-bold text-slate-900 dark:text-white">
                                                        <div className="flex items-center gap-2">
                                                            {formatCurrency(row.price)}
                                                            {row.isVerified && (
                                                                <span className="material-symbols-outlined text-green-500 text-sm" title="Doğrulanmış Fiyat">verified</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-3 text-slate-400 text-xs">{new Date(row.date).toLocaleDateString()}</td>
                                                </tr>
                                            ))}
                                            {!getProcessedRecords().length && (
                                                <tr><td colSpan="6" className="p-8 text-center text-slate-400 italic">Kayıt bulunamadı.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
